// src/context/AnonymousUserContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { trackHits } from '../hitscheck'; // Adjust path if necessary
import { auth } from '../firebaseConfig';   // Adjust path if necessary

interface AnonContextType {
  availableHits: number | undefined;
  isBlocked: boolean;
  isLoading: boolean;
}

const AnonymousUserContext = createContext<AnonContextType | undefined>(undefined);

export const AnonymousUserProvider = ({ children }: { children: ReactNode }) => {
  const [availableHits, setAvailableHits] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  // --- NEW: Read the security setting from the .env file ---
  const isSecurityEnabled = import.meta.env.VITE_SIGNUP_SECURITY === 'on';

  useEffect(() => {
    // If security is disabled, we don't need to run any checks.
    if (!isSecurityEnabled) {
      setIsLoading(false);
      return;
    }

    const unsubscribe = auth.onAuthStateChanged(user => {
      if (!user) {
        const checkAnonymousDevice = async () => {
          setIsLoading(true);
          try {
            const { availableHits: hits } = await trackHits('check');
            setAvailableHits(hits);
          } catch (error) {
            console.error("Error tracking anonymous device:", error);
            // === MODIFIED: Fallback to a non-blocking value (1) to match our new system ===
            setAvailableHits(1);
          } finally {
            setIsLoading(false);
          }
        };
        checkAnonymousDevice();
      } else {
        setIsLoading(false);
        setAvailableHits(undefined);
      }
    });
    return () => unsubscribe();
  }, [isSecurityEnabled]); // Re-run effect if the setting changes (though it won't during runtime)

  // --- UPDATED: The 'isBlocked' variable now respects the master switch ---
  // The device is only considered blocked if security is enabled AND their hits are zero or less.
  const isBlocked = isSecurityEnabled && !isLoading && availableHits !== undefined && availableHits <= 0;

  const value = { availableHits, isBlocked, isLoading };

  return (
    <AnonymousUserContext.Provider value={value}>
      {children}
    </AnonymousUserContext.Provider>
  );
};

export const useAnonymousUser = () => {
  const context = useContext(AnonymousUserContext);
  if (context === undefined) {
    throw new Error('useAnonymousUser must be used within an AnonymousUserContext');
  }
  return context;
};